import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "modalBody", "copyButton", "copyIcon", "copyText"]
  static values = { patientId: Number }

  connect() {
    // Escuchar eventos del modal de Bootstrap 4 si el target existe
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener('show.bs.modal', this.handleModalShow.bind(this))
      this.modalTarget.addEventListener('hidden.bs.modal', this.handleModalHidden.bind(this))
    }

    // Prevenir doble llamada a handleModalShow
    this.modalShowHandled = false

    // Usar event delegation para el botón de copiar que se carga dinámicamente
    const modal = this.getModal()
    if (modal) {
      modal.addEventListener('click', this.handleModalClick.bind(this))
    }
  }

  disconnect() {
    const modal = this.getModal()
    if (modal) {
      modal.removeEventListener('click', this.handleModalClick.bind(this))
    }
  }

  handleModalClick(event) {
    // Verificar si el click fue en el botón de copiar
    const copyButton = event.target.closest('[data-action*="copyLink"]')
    if (copyButton) {
      event.preventDefault()
      event.stopPropagation()
      this.copyLinkWithButton(copyButton)
    }
  }

  // Helper para obtener el modal (target o búsqueda manual)
  getModal() {
    if (this.hasModalTarget) {
      return this.modalTarget
    }
    // Buscar manualmente si no está como target
    return this.element.querySelector('#patientAccessTokenModal') || document.getElementById('patientAccessTokenModal')
  }

  // Helper para obtener el modalBody (target o búsqueda manual)
  getModalBody() {
    if (this.hasModalBodyTarget) {
      return this.modalBodyTarget
    }
    // Buscar manualmente si no está como target
    const modal = this.getModal()
    if (modal) {
      return modal.querySelector('[data-patient-access-token-modal-target="modalBody"]')
    }
    return this.element.querySelector('[data-patient-access-token-modal-target="modalBody"]')
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener('show.bs.modal', this.handleModalShow.bind(this))
      this.modalTarget.removeEventListener('hidden.bs.modal', this.handleModalHidden.bind(this))
    }
  }

  open(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.relatedButton = event?.currentTarget || event?.target?.closest('button')

    // Verificar que tenemos el modal
    const modal = this.getModal()
    if (!modal) {
      console.error('Error: No se pudo encontrar el modal con ID patientAccessTokenModal')
      return
    }

    // Verificar que tenemos el modalBody
    const modalBody = this.getModalBody()
    if (!modalBody) {
      console.error('Error: No se pudo encontrar el modalBody target')
      return
    }

    // Guardar referencias temporalmente para usar en otros métodos
    this._modal = modal
    this._modalBody = modalBody

    // Mostrar el modal usando Bootstrap 4 (sin jQuery)
    this.showModal()

    // Llamar directamente a handleModalShow ya que el evento show.bs.modal puede no dispararse
    // cuando creamos el modal manualmente
    setTimeout(() => {
      this.handleModalShow({ relatedTarget: this.relatedButton })
    }, 100)
  }

  showModal() {
    const modal = this.getModal()
    if (!modal) return

    // Intentar usar Bootstrap 4 con jQuery si está disponible
    if (typeof $ !== 'undefined' && $.fn.modal) {
      // Remover aria-hidden antes de mostrar para evitar warnings
      modal.removeAttribute('aria-hidden')
      $(modal).modal('show')
      return
    }

    // Intentar usar Bootstrap 5 si está disponible
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      // Verificar si ya existe una instancia
      let bsModal = bootstrap.Modal.getInstance(modal)
      if (!bsModal) {
        bsModal = new bootstrap.Modal(modal)
      }
      // Remover aria-hidden antes de mostrar
      modal.removeAttribute('aria-hidden')
      bsModal.show()
      return
    }

    // Fallback manual: mostrar el modal sin aria-hidden hasta que esté completamente visible
    modal.style.display = 'block'
    modal.classList.add('show')
    // NO establecer aria-hidden aquí, dejarlo sin el atributo o establecerlo después
    document.body.classList.add('modal-open')

    // Crear el backdrop
    if (!document.getElementById('patientAccessTokenModalBackdrop')) {
      const backdrop = document.createElement('div')
      backdrop.className = 'modal-backdrop fade show'
      backdrop.id = 'patientAccessTokenModalBackdrop'
      backdrop.addEventListener('click', () => this.close())
      document.body.appendChild(backdrop)
    }

    // Establecer aria-hidden='false' ANTES de enfocar elementos
    modal.removeAttribute('aria-hidden')
    modal.setAttribute('aria-hidden', 'false')

    // Enfocar el primer elemento enfocable del modal después de un pequeño delay
    requestAnimationFrame(() => {
      const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        firstFocusable.focus()
      }
    })

    // Trigger el evento show.bs.modal manualmente
    const showEvent = new Event('show.bs.modal', { bubbles: true })
    modal.dispatchEvent(showEvent)
  }

  hideModal() {
    const modal = this.getModal()
    if (!modal) return

    // Intentar usar Bootstrap 4 con jQuery si está disponible
    if (typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide')
      return
    }

    // Intentar usar Bootstrap 5 si está disponible
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
      const bsModal = bootstrap.Modal.getInstance(modal)
      if (bsModal) {
        bsModal.hide()
        return
      }
    }

    // Fallback manual: ocultar el modal
    // Primero establecer aria-hidden antes de quitar el foco
    modal.setAttribute('aria-hidden', 'true')

    // Luego ocultar el modal
    modal.style.display = 'none'
    modal.classList.remove('show')
    document.body.classList.remove('modal-open')

    // Remover el backdrop
    const backdrop = document.getElementById('patientAccessTokenModalBackdrop')
    if (backdrop) {
      backdrop.remove()
    }

    // Remover cualquier otro backdrop que pueda quedar
    const otherBackdrops = document.querySelectorAll('.modal-backdrop')
    otherBackdrops.forEach(b => {
      if (b.id !== 'patientAccessTokenModalBackdrop') {
        b.remove()
      }
    })
  }

  handleModalShow(event) {
    // Prevenir doble llamada
    if (this.modalShowHandled) return
    this.modalShowHandled = true

    const button = event.relatedTarget || this.relatedButton
    if (!button) {
      this.modalShowHandled = false
      return
    }

    // Obtener el modalBody (target o búsqueda manual)
    const modalBody = this.getModalBody()
    if (!modalBody) {
      this.modalShowHandled = false
      return
    }

    const tokenUrl = button.getAttribute('href')
    // Leer patientId desde el botón o usar el valor del controlador
    let patientId = this.patientIdValue
    if (button.dataset.patientAccessTokenModalPatientIdValue) {
      patientId = parseInt(button.dataset.patientAccessTokenModalPatientIdValue)
    } else if (button.dataset.patientId) {
      patientId = parseInt(button.dataset.patientId)
    }

    // Resetear el contenido del modal
    modalBody.innerHTML = '<div class="text-center"><div class="spinner-border" role="status"><span class="sr-only">Cargando...</span></div><p class="mt-2">Por favor espere...</p></div>'

    // Si el botón tiene un href, es para ver un token existente
    if (tokenUrl && tokenUrl.includes('patient_access_tokens') && !tokenUrl.includes('method=delete')) {
      this.loadExistingToken(tokenUrl)
    } else if (patientId) {
      this.createNewToken(patientId)
    } else {
      modalBody.innerHTML = '<div class="alert alert-danger">No se pudo identificar el paciente. Recargue la página.</div>'
      this.modalShowHandled = false
    }
  }

  handleModalHidden() {
    const modalBody = this.getModalBody()
    if (modalBody) {
      modalBody.innerHTML = '<div class="text-center"><div class="spinner-border" role="status"><span class="sr-only">Cargando...</span></div></div>'
    }
    this.modalShowHandled = false
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.hideModal()

    // Trigger el evento hidden.bs.modal
    if (this.hasModalTarget) {
      const hiddenEvent = new Event('hidden.bs.modal', { bubbles: true })
      this.modalTarget.dispatchEvent(hiddenEvent)
    }
  }

  loadExistingToken(tokenUrl) {
    fetch(tokenUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/html'
      },
      credentials: 'same-origin', // Asegurar que se envíen las cookies de sesión
      redirect: 'manual' // No seguir redirecciones automáticamente
    })
    .then(response => {
      // Detectar redirección (ocurre cuando la sesión expiró)
      if (response.type === 'opaqueredirect' || response.status === 0) {
        throw new Error('SESSION_EXPIRED')
      }
      
      // Manejar redirecciones explícitas
      if (response.redirected || (response.status >= 300 && response.status < 400)) {
        throw new Error('SESSION_EXPIRED')
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      const modalBody = this.getModalBody()
      if (modalBody) {
        // Verificar que la respuesta es un fragmento HTML y no una página completa
        if (html.includes('<!DOCTYPE') || html.includes('<html')) {
          console.error('Received full HTML page instead of fragment - session may have expired')
          modalBody.innerHTML = '<div class="alert alert-warning">Tu sesión ha expirado. Por favor, <a href="javascript:window.location.reload()">recarga la página</a> e intenta nuevamente.</div>'
        } else {
          modalBody.innerHTML = html
        }
      }
      this.modalShowHandled = false
    })
    .catch(error => {
      const modalBody = this.getModalBody()
      if (modalBody) {
        if (error.message === 'SESSION_EXPIRED') {
          modalBody.innerHTML = '<div class="alert alert-warning">Tu sesión ha expirado. Por favor, <a href="javascript:window.location.reload()">recarga la página</a> e intenta nuevamente.</div>'
        } else {
          modalBody.innerHTML = `<div class="alert alert-danger">Error al cargar el token: ${error.message}</div>`
        }
      }
      this.modalShowHandled = false
    })
  }

  createNewToken(patientId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const url = `/patients/${patientId}/patient_access_tokens`
    const modalBody = this.getModalBody()

    if (!csrfToken) {
      if (modalBody) {
        modalBody.innerHTML = '<div class="alert alert-danger">Error: No se encontró el token CSRF. Recargue la página.</div>'
      }
      this.modalShowHandled = false
      return
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/html',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      credentials: 'same-origin', // Asegurar que se envíen las cookies de sesión
      redirect: 'manual' // No seguir redirecciones automáticamente
    })
    .then(response => {
      // Detectar redirección (ocurre cuando la sesión expiró o CSRF es inválido)
      if (response.type === 'opaqueredirect' || response.status === 0) {
        throw new Error('SESSION_EXPIRED')
      }
      
      // Manejar redirecciones explícitas (301, 302, 303, 307, 308)
      if (response.redirected || (response.status >= 300 && response.status < 400)) {
        throw new Error('SESSION_EXPIRED')
      }
      
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`)
        })
      }
      return response.text()
    })
    .then(html => {
      const modalBody = this.getModalBody()
      if (modalBody) {
        if (html && html.length > 0) {
          // Verificar que la respuesta es un fragmento HTML y no una página completa
          // Una página completa tendría <!DOCTYPE o <html
          if (html.includes('<!DOCTYPE') || html.includes('<html')) {
            console.error('Received full HTML page instead of fragment - session may have expired')
            modalBody.innerHTML = '<div class="alert alert-warning">Tu sesión ha expirado. Por favor, <a href="javascript:window.location.reload()">recarga la página</a> e intenta nuevamente.</div>'
          } else {
            modalBody.innerHTML = html
          }
        } else {
          modalBody.innerHTML = '<div class="alert alert-warning">Respuesta vacía del servidor. Intente nuevamente.</div>'
        }
      }
      this.modalShowHandled = false
    })
    .catch(error => {
      const modalBody = this.getModalBody()
      if (modalBody) {
        let errorMsg = 'Error al generar el token de acceso'
        
        if (error.message === 'SESSION_EXPIRED') {
          errorMsg = 'Tu sesión ha expirado.'
          modalBody.innerHTML = `<div class="alert alert-warning">${errorMsg} Por favor, <a href="javascript:window.location.reload()">recarga la página</a> e intenta nuevamente.</div>`
          this.modalShowHandled = false
          return
        }
        
        if (error.message.includes('HTTP 401') || error.message.includes('HTTP 403')) {
          errorMsg = 'No tiene permisos para realizar esta acción.'
        } else if (error.message.includes('HTTP 422')) {
          errorMsg = 'Error de validación al crear el token.'
        } else if (error.message.includes('HTTP 500')) {
          errorMsg = 'Error interno del servidor. Revise los logs del servidor.'
        } else if (error.message.includes('Failed to fetch')) {
          errorMsg = 'No se pudo conectar al servidor. Verifique su conexión y recargue la página.'
        }

        modalBody.innerHTML = `<div class="alert alert-danger">${errorMsg}<br><small>${error.message}</small></div>`
      }
      this.modalShowHandled = false
    })
  }

  copyLink(event) {
    event.preventDefault()
    event.stopPropagation()
    this.copyLinkWithButton(event.currentTarget)
  }

  copyLinkWithButton(button) {
    const linkInput = document.getElementById('accessLink')
    if (!linkInput) return

    // Usar Clipboard API moderno si está disponible
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(linkInput.value).then(() => {
        this.showCopySuccess(button)
      }).catch(() => {
        // Fallback al método antiguo si falla
        this.fallbackCopy(linkInput, button)
      })
    } else {
      // Fallback al método antiguo
      this.fallbackCopy(linkInput, button)
    }
  }

  fallbackCopy(linkInput, button) {
    linkInput.select()
    linkInput.setSelectionRange(0, 99999) // Para móviles
    try {
      document.execCommand('copy')
      this.showCopySuccess(button)
    } catch (err) {
      // Silenciar el error, solo intentar copiar
    }
  }

  showCopySuccess(button) {
    const copyIcon = button.querySelector('[data-patient-access-token-modal-target="copyIcon"]')
    
    if (copyIcon) {
      const originalClass = copyIcon.className
      copyIcon.className = 'fas fa-check'
      
      setTimeout(() => {
        copyIcon.className = originalClass
      }, 2000)
    }
  }

  sendEmail(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const tokenId = button.getAttribute('data-token-id')
    const patientId = button.getAttribute('data-patient-id')

    if (!tokenId || !patientId) {
      alert('Error: No se pudo identificar el token o el paciente.')
      return
    }

    // Deshabilitar el botón durante el envío
    button.disabled = true
    const originalHTML = button.innerHTML
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Enviando...'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const url = `/patients/${patientId}/patient_access_tokens/${tokenId}/send_email`

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        button.innerHTML = '<i class="fas fa-check mr-2"></i>Enviado'
        button.classList.remove('btn-primary')
        button.classList.add('btn-success')
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.disabled = false
          button.classList.remove('btn-success')
          button.classList.add('btn-primary')
        }, 3000)
      } else {
        alert('Error: ' + (data.errors || data.message || 'No se pudo enviar el correo'))
        button.innerHTML = originalHTML
        button.disabled = false
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Error al enviar el correo. Por favor, intente nuevamente.')
      button.innerHTML = originalHTML
      button.disabled = false
    })
  }

  sendWhatsApp(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const tokenId = button.getAttribute('data-token-id')
    const chatroomId = button.getAttribute('data-chatroom-id')
    const accessUrl = button.getAttribute('data-access-url')

    if (!tokenId || !chatroomId || !accessUrl) {
      alert('Error: Faltan datos necesarios para enviar por WhatsApp.')
      return
    }

    // Deshabilitar el botón durante el envío
    button.disabled = true
    const originalHTML = button.innerHTML
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Enviando...'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const url = `/patients/${button.getAttribute('data-patient-id')}/patient_access_tokens/${tokenId}/send_whatsapp`

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        chatroom_id: chatroomId
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        button.innerHTML = '<i class="fas fa-check mr-2"></i>Enviado'
        button.classList.remove('btn-success')
        button.classList.add('btn-success', 'disabled')
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.disabled = false
          button.classList.remove('disabled')
        }, 3000)
      } else {
        alert('Error: ' + (data.errors || data.message || 'No se pudo enviar el mensaje por WhatsApp'))
        button.innerHTML = originalHTML
        button.disabled = false
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Error al enviar por WhatsApp. Por favor, intente nuevamente.')
      button.innerHTML = originalHTML
      button.disabled = false
    })
  }
}
